import { Link, NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom'
import {
  AppShell as MantineAppShell,
  Button,
  Center,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import type { User } from 'firebase/auth'
import type { Role } from '@racedash/shared'
import { ColorSchemeToggle } from '@racedash/shared/ui'
import { useAuth } from './auth/AuthProvider'
import { useOrgs } from './org/useOrgs'
import type { OrgSummary } from './org/useOrgs'
import SignIn from './routes/SignIn'

export interface ShellContext {
  user: User
  orgs: OrgSummary[]
  /** The organisation named in the URL, if the user is a member of it. */
  currentOrg: OrgSummary | undefined
  role: Role | null
}

export function useShell(): ShellContext {
  return useOutletContext<ShellContext>()
}

/** A whole-page message, for the states that have no shell to sit inside. */
function Standalone({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <Center mih="100vh" p="md">
      {title ? (
        <Paper withBorder p="xl" w={440} maw="100%">
          <Stack gap="xs">
            <Title order={1} size="h3">
              {title}
            </Title>
            {children}
          </Stack>
        </Paper>
      ) : (
        children
      )}
    </Center>
  )
}

export default function AppShell() {
  const { state, signOutNow } = useAuth()
  const { orgId } = useParams()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  const { orgs, error } = useOrgs(uid)

  if (state.status === 'loading') {
    return (
      <Standalone>
        <Text c="dimmed">Loading…</Text>
      </Standalone>
    )
  }

  if (state.status === 'error') {
    return (
      <Standalone title="Something went wrong">
        <Text c="dimmed" size="sm">
          {state.message}
        </Text>
        <Button mt="md" variant="default" onClick={() => void signOutNow()}>
          Sign out and try again
        </Button>
      </Standalone>
    )
  }

  if (state.status === 'signed-out') return <SignIn />

  if (error) {
    return (
      <Standalone title="Couldn't load your organisations">
        <Text c="dimmed" size="sm">
          {error}
        </Text>
      </Standalone>
    )
  }

  if (!orgs) {
    return (
      <Standalone>
        <Text c="dimmed">Loading…</Text>
      </Standalone>
    )
  }

  const currentOrg = orgs.find((o) => o.id === orgId)
  const context: ShellContext = {
    user: state.user,
    orgs,
    currentOrg,
    role: currentOrg?.role ?? null,
  }

  return (
    <MantineAppShell header={{ height: 56 }} padding={0}>
      <MantineAppShell.Header>
        <Container size="lg" h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap">
            <Group gap="lg" wrap="nowrap" style={{ minWidth: 0 }}>
              <Link className="wordmark" to="/">
                RaceDash<span>.</span>live
              </Link>
              {/* Always rendered. It doubles as the "where am I" label when
                  there is only one organisation, and it is where you go to make
                  another — which previously had no entry point at all. */}
              <Group gap={2} component="nav" wrap="nowrap" style={{ overflowX: 'auto' }}>
                {orgs.map((org) => (
                  <NavLink
                    key={org.id}
                    to={`/o/${org.id}`}
                    className={({ isActive }) =>
                      isActive ? 'org-switch-item active' : 'org-switch-item'
                    }
                  >
                    {org.kind === 'personal' ? 'Personal' : org.name}
                  </NavLink>
                ))}
                <NavLink
                  to="/organisations/new"
                  className="org-switch-item new"
                  title="Create an organisation"
                >
                  + New
                </NavLink>
              </Group>
            </Group>
            <Group gap="xs" wrap="nowrap">
              <Text c="dimmed" size="xs" visibleFrom="sm">
                {state.user.displayName ?? 'Signed in'}
              </Text>
              <ColorSchemeToggle />
              <Button variant="subtle" color="gray" size="sm" onClick={() => void signOutNow()}>
                Sign out
              </Button>
            </Group>
          </Group>
        </Container>
      </MantineAppShell.Header>

      <MantineAppShell.Main>
        <Container size="lg" py="xl">
          <Outlet context={context} />
        </Container>
      </MantineAppShell.Main>
    </MantineAppShell>
  )
}
